import { useEffect } from "react";
import { useViewContextStore } from "../store/view-context-store";

interface UseRegisterViewContextProps {
  key: string; // Unique key for this view (e.g., "calendar", "appointment-list")
  shortContext: string; // Always injected via runtime onNew (~50 tokens)
  longContext?: string; // Optional, retrieved via tool (~500 tokens)
}

/**
 * Register view context for AI assistant
 *
 * Short context will be automatically included in AI messages via runtime onNew.
 * Long context can be retrieved on-demand using getDetailedViewContext tool.
 *
 * @param key - Unique identifier for this view
 * @param shortContext - Lightweight context always included in AI messages
 * @param longContext - Optional detailed context retrieved via tool call
 */
export function useRegisterViewContext({
  key,
  shortContext,
  longContext,
}: UseRegisterViewContextProps) {
  const setViewContext = useViewContextStore((state) => state.setViewContext);
  const clearViewContext = useViewContextStore((state) => state.clearViewContext);

  useEffect(() => {
    setViewContext(key, shortContext, longContext);

    // Cleanup: remove context when component unmounts
    return () => {
      clearViewContext(key);
    };
  }, [key, shortContext, longContext, setViewContext, clearViewContext]);
}
